/**
 * Cost tracking, cost guards and provenance (spec §44, §59, §60).
 *
 * Every external generation call records what it cost, in provider-native units
 * for audit and normalised HipNumber units for control. Cost guards make
 * runaway generation impossible (spec §60): a campaign, an image, a video, the
 * retry count and the tenant's month are all ceilinged, and the check is a pure
 * function so the planner (before enqueue) and the worker (before a retry or a
 * fallback) enforce the SAME limits.
 *
 * Provenance (spec §44) is the row that makes an output reproducible, billable
 * and debuggable: it ties an asset version back to its specs, snapshots, source
 * media, provider run, cost and creator.
 */

import { z } from 'zod';
import { MoneySchema, type Money } from './ids.js';

// ---------------------------------------------------------------------------
// Cost guards (spec §60)
// ---------------------------------------------------------------------------

export const CostGuardsSchema = z
  .object({
    maxCampaignCost: MoneySchema,
    maxImageCost: MoneySchema.optional(),
    maxVideoCost: MoneySchema.optional(),
    maxVideoDurationMs: z.number().int().positive().optional(),
    maxVariants: z.number().int().positive().optional(),
    maxAutoRetries: z.number().int().nonnegative().optional(),
    fallbackCostCeiling: MoneySchema.optional(),
    tenantMonthlyCeiling: MoneySchema.optional(),
  })
  .strict();
export type CostGuards = z.infer<typeof CostGuardsSchema>;

export const COST_GUARD_BREACHES = [
  'CAMPAIGN_CEILING',
  'IMAGE_CEILING',
  'VIDEO_CEILING',
  'VIDEO_DURATION',
  'TENANT_MONTHLY_CEILING',
  'RETRY_CEILING',
] as const;
export type CostGuardBreach = (typeof COST_GUARD_BREACHES)[number];

export interface CostCheckInput {
  guards: CostGuards;
  /** What this one generation is estimated to cost (normalised). */
  estimate: Money;
  mediaType: 'IMAGE' | 'VIDEO';
  /** Already spent on this campaign / this tenant this month (normalised). */
  campaignSpent: Money;
  tenantMonthSpent?: Money;
  videoDurationMs?: number;
  /** Retries already attempted for this asset. */
  retriesUsed?: number;
}

function sameCurrency(a: Money, b: Money): boolean {
  return a.currency === b.currency;
}

/**
 * Returns the first guard a proposed generation would breach, or null when it
 * is within every limit. A currency mismatch is treated as a breach rather
 * than compared numerically - the caller must normalise first.
 */
export function checkCostGuards(input: CostCheckInput): CostGuardBreach | null {
  const { guards, estimate } = input;

  if (input.mediaType === 'IMAGE' && guards.maxImageCost) {
    if (!sameCurrency(estimate, guards.maxImageCost) || estimate.amountMinor > guards.maxImageCost.amountMinor) return 'IMAGE_CEILING';
  }
  if (input.mediaType === 'VIDEO' && guards.maxVideoCost) {
    if (!sameCurrency(estimate, guards.maxVideoCost) || estimate.amountMinor > guards.maxVideoCost.amountMinor) return 'VIDEO_CEILING';
  }
  if (input.mediaType === 'VIDEO' && guards.maxVideoDurationMs !== undefined && input.videoDurationMs !== undefined) {
    if (input.videoDurationMs > guards.maxVideoDurationMs) return 'VIDEO_DURATION';
  }

  if (!sameCurrency(estimate, input.campaignSpent) || input.campaignSpent.amountMinor + estimate.amountMinor > guards.maxCampaignCost.amountMinor) {
    return 'CAMPAIGN_CEILING';
  }

  if (guards.tenantMonthlyCeiling && input.tenantMonthSpent) {
    if (!sameCurrency(estimate, guards.tenantMonthlyCeiling) || input.tenantMonthSpent.amountMinor + estimate.amountMinor > guards.tenantMonthlyCeiling.amountMinor) {
      return 'TENANT_MONTHLY_CEILING';
    }
  }

  if (guards.maxAutoRetries !== undefined && input.retriesUsed !== undefined && input.retriesUsed > guards.maxAutoRetries) {
    return 'RETRY_CEILING';
  }

  return null;
}

// ---------------------------------------------------------------------------
// Provenance (spec §44)
// ---------------------------------------------------------------------------

export const AssetProvenanceSchema = z
  .object({
    tenantId: z.string().min(1),
    auctionId: z.string().min(1),
    campaignId: z.string().min(1),
    assetId: z.string().min(1),
    assetVersionId: z.string().min(1),
    creativeSpecId: z.string().min(1),
    templateId: z.string().min(1).optional(),
    templateVersion: z.number().int().positive().optional(),
    brandSnapshotId: z.string().min(1),
    auctionTruthSnapshotId: z.string().min(1),
    sourceAssetIds: z.array(z.string().min(1)),
    providerRunId: z.string().min(1).optional(),
    cost: MoneySchema.optional(),
    generationDurationMs: z.number().int().nonnegative().optional(),
    renderVersion: z.string().optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type AssetProvenance = z.infer<typeof AssetProvenanceSchema>;
